<template>
  <v-container fluid>
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon left color="orange">mdi-format-list-numbered</v-icon>
        <span>Gestión de Consecutivos DIAN</span>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="refreshData" :loading="loading">
          <v-icon left>mdi-refresh</v-icon>
          Actualizar
        </v-btn>
        <v-btn color="success" @click="createNew" class="ml-2">
          <v-icon left>mdi-plus</v-icon>
          Nuevo Consecutivo
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Información de la empresa -->
        <v-alert v-if="company" type="info" outlined class="mb-4">
          <div class="d-flex align-center">
            <v-icon left>mdi-domain</v-icon>
            <div>
              <strong>{{ company.PartyName }}</strong
              ><br />
              <small
                >NIT: {{ company.PartyIdentification }} | Ambiente:
                {{ company.environment === 1 ? "Producción" : "Pruebas" }}</small
              >
            </div>
          </div>
        </v-alert>

        <!-- Tabla de consecutivos -->
        <v-data-table
          :headers="headers"
          :items="consecutivos"
          :loading="loading"
          class="elevation-1"
          :items-per-page="10"
        >
          <template v-slot:[`item.documentType`]="{ item }">
            <v-chip :color="getDocTypeColor(item.documentType)" small dark>
              {{ getDocTypeName(item.documentType) }}
            </v-chip>
          </template>

          <template v-slot:[`item.Actual`]="{ item }">
            <div class="text-center">
              <span class="text-h6 font-weight-bold">{{ item.Actual }}</span>
              <div class="text-caption grey--text">Actual</div>
            </div>
          </template>

          <template v-slot:[`item.range`]="{ item }">
            <div class="text-center">
              <div class="text-body-2">
                {{ item.AuthorizationFrom }} - {{ item.AuthorizationTo }}
              </div>
              <div class="text-caption grey--text">Rango autorizado</div>
            </div>
          </template>

          <template v-slot:[`item.available`]="{ item }">
            <div class="text-center">
              <v-progress-circular
                :value="getUsagePercentage(item)"
                :color="getUsageColor(item)"
                size="60"
                width="4"
              >
                {{ getAvailableCount(item) }}
              </v-progress-circular>
              <div class="text-caption grey--text mt-1">Disponibles</div>
            </div>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip :color="getStatusColor(item)" small :outlined="!isActive(item)">
              {{ getStatusText(item) }}
            </v-chip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  @click="editConsecutive(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Editar consecutivo</span>
            </v-tooltip>
          </template>

          <template v-slot:no-data>
            <div class="text-center py-8">
              <v-icon size="64" color="grey lighten-2">mdi-format-list-numbered</v-icon>
              <p class="text-h6 grey--text mt-4">No hay consecutivos configurados</p>
              <p class="text-body-2 grey--text">
                Configure los rangos de numeración para los documentos DIAN
              </p>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Diálogo para editar consecutivo -->
    <v-dialog v-model="editDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="pa-4">
          <v-icon left color="primary">mdi-pencil</v-icon>
          {{ isCreating ? "Nuevo Consecutivo" : "Editar Consecutivo" }}
          {{
            selectedItem && !isCreating ? getDocTypeName(selectedItem.documentType) : ""
          }}
        </v-card-title>

        <v-card-text class="pa-4">
          <v-form v-if="selectedItem" ref="editForm" v-model="formValid">
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="selectedItem.documentType"
                  label="Tipo de documento"
                  outlined
                  :rules="[rules.required]"
                  hint=""
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="selectedItem.Prefix"
                  label="Prefijo"
                  outlined
                  :rules="[rules.required]"
                  hint="Prefijo del documento (ej: SETP)"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="selectedItem.Actual"
                  label="Número Actual"
                  type="number"
                  outlined
                  :rules="[rules.required, rules.validRange]"
                  :hint="`Rango: ${selectedItem.AuthorizationFrom} - ${selectedItem.AuthorizationTo}`"
                  persistent-hint
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.AuthorizationFrom"
                  label="Rango Desde"
                  type="number"
                  outlined
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.AuthorizationTo"
                  label="Rango Hasta"
                  type="number"
                  outlined
                  :rules="[rules.required, rules.rangeValid]"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.InvoiceAuthorization"
                  label="Resolución DIAN"
                  outlined
                  hint="Número de resolución de la DIAN"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.SoftwareSecurityCode"
                  label="Código de Seguridad"
                  outlined
                  hint="Código de seguridad del software"
                  persistent-hint
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.StartDate"
                  label="Fecha Inicio"
                  type="date"
                  outlined
                  hint="Fecha de inicio de vigencia"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.EndDate"
                  label="Fecha Fin"
                  type="date"
                  outlined
                  hint="Fecha de fin de vigencia"
                  persistent-hint
                />
              </v-col>
            </v-row>

            <v-alert v-if="getAvailableCount(selectedItem) < 100" type="warning" outlined>
              <v-icon left>mdi-alert</v-icon>
              <strong>Atención:</strong> Quedan pocos números disponibles en este rango.
              Considere solicitar una nueva resolución a la DIAN.
            </v-alert>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="cancelEdit">Cancelar</v-btn>
          <v-btn
            color="primary"
            @click="saveConsecutive"
            :disabled="!formValid"
            :loading="saving"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { webserver } from "@/services/webserver.js";

export default {
  name: "ConsecutiveManager",

  data() {
    return {
      loading: false,
      saving: false,
      editDialog: false,
      formValid: false,
      isCreating: false,

      company: null,
      consecutivos: [],
      selectedItem: null,

      headers: [
        { text: "Tipo Documento", value: "documentType", width: "15%" },
        { text: "Prefijo", value: "Prefix", align: "center", width: "15%" },
        { text: "Número Actual", value: "Actual", align: "center", width: "15%" },
        { text: "Rango", value: "range", align: "center", width: "20%" },

        { text: "Estado", value: "status", align: "center", width: "15%" },
        {
          text: "Acciones",
          value: "actions",
          align: "center",
          sortable: false,
          width: "20%",
        },
      ],

      rules: {
        required: (v) => !!v || "Campo requerido",
        validRange: (v) => {
          if (!this.selectedItem) return true;
          const num = parseInt(v);
          const from = parseInt(this.selectedItem.AuthorizationFrom);
          const to = parseInt(this.selectedItem.AuthorizationTo);
          return (num >= from && num <= to) || `Debe estar entre ${from} y ${to}`;
        },
        rangeValid: (v) => {
          if (!this.selectedItem) return true;
          const to = parseInt(v);
          const from = parseInt(this.selectedItem.AuthorizationFrom);
          return to > from || "El rango final debe ser mayor al inicial";
        },
      },
    };
  },

  mounted() {
    this.loadData();
  },

  methods: {
    async loadData() {
      this.loading = true;
      try {
        await Promise.all([this.loadCompany(), this.loadConsecutivos()]);
      } catch (error) {
        console.error("Error cargando datos:", error);
        this.$store.commit("setSnackbar", {
          text: "Error cargando datos de consecutivos",
          color: "error",
          timeout: 3000,
        });
      } finally {
        this.loading = false;
      }
    },

    async loadCompany() {
      const qry = {
        store: window.store.store_id,
        table: "f_companies",
        filters: [
          {
            field: "store_code",
            operator: "=",
            value: window.store.store_id,
          },
        ],
      };

      return new Promise((resolve) => {
        webserver("get_dataTable", qry, (data) => {
          console.log("🏢 Datos empresa recibidos:", data);

          // ✅ ARREGLO: Manejar correctamente la respuesta
          if (Array.isArray(data) && data.length > 0) {
            this.company = data[0];
          } else if (data && typeof data === "object" && !Array.isArray(data)) {
            // Si es un objeto directo, usarlo
            this.company = data;
          } else {
            this.company = null;
          }

          console.log("✅ Empresa procesada:", this.company);
          resolve();
        });
      });
    },

    async loadConsecutivos() {
      const qry = {
        store: window.store.store_id,
        table: "f_consecutive",
        filters: [
          {
            field: "store",
            operator: "=",
            value: window.store.store_id,
          },
        ],
      };

      return new Promise((resolve) => {
        webserver("get_dataTable", qry, (data) => {
          console.log("📊 Datos consecutivos recibidos:", data);

          // ✅ ARREGLO: Asegurar que sea siempre un array
          if (Array.isArray(data)) {
            this.consecutivos = data;
          } else {
            // Si no hay datos o es null/undefined
            this.consecutivos = [];
          }

          console.log("✅ Consecutivos procesados:", this.consecutivos);
          resolve();
        });
      });
    },

    refreshData() {
      this.loadData();
    },

    createNew() {
      this.isCreating = true;
      this.selectedItem = {
        store: window.store.store_id,
        documentType: "FE",
        Actual: 1,
        Prefix: "SETP",
        AuthorizationFrom: 1,
        AuthorizationTo: 5000,
        InvoiceAuthorization: "",
        SoftwareSecurityCode: "",
        StartDate: "",
        EndDate: "",
      };
      this.editDialog = true;
    },

    editConsecutive(item) {
      this.isCreating = false;
      this.selectedItem = { ...item };
      this.editDialog = true;
    },

    cancelEdit() {
      this.editDialog = false;
      this.selectedItem = null;
      this.isCreating = false;
      if (this.$refs.editForm) {
        this.$refs.editForm.resetValidation();
      }
    },

    async saveConsecutive() {
      if (!this.$refs.editForm.validate()) return;

      this.saving = true;
      try {
        const method = this.isCreating ? "put_dataTable" : "put_dataTable";
        const qry = {
          store: window.store.store_id,
          table: "f_consecutive",
          data: [this.selectedItem],
        };

        console.log(
          `📤 ${this.isCreating ? "Creando" : "Actualizando"} consecutivo:`,
          qry
        );

        await new Promise((resolve, reject) => {
          webserver(
            method,
            qry,
            (data) => {
              console.log("📥 Respuesta del servidor:", data);

              if (data && (data.success === true || data.affectedRows > 0)) {
                resolve(data);
              } else {
                reject(
                  new Error(
                    data?.error ||
                      data?.message ||
                      `Error ${this.isCreating ? "creando" : "actualizando"} consecutivo`
                  )
                );
              }
            },
            (error) => {
              console.error("❌ Error en webserver:", error);
              reject(error);
            }
          );
        });

        this.$store.commit("setSnackbar", {
          text: `Consecutivo ${this.isCreating ? "creado" : "actualizado"} correctamente`,
          color: "success",
          timeout: 3000,
        });

        this.editDialog = false;
        this.selectedItem = null;
        this.isCreating = false;
        this.loadConsecutivos();
      } catch (error) {
        console.error("Error guardando consecutivo:", error);
        this.$store.commit("setSnackbar", {
          text: `Error ${this.isCreating ? "creando" : "actualizando"} consecutivo: ${
            error.message
          }`,
          color: "error",
          timeout: 5000,
        });
      } finally {
        this.saving = false;
      }
    },

    // Métodos de utilidad
    getDocTypeName(docType) {
      const types = {
        FE: "Factura Electrónica",
        FC: "Factura de Contingencia",
        NC: "Nota de Crédito",
        ND: "Nota de Débito",
      };
      return types[docType] || docType;
    },

    getDocTypeColor(docType) {
      const colors = {
        FE: "primary",
        FC: "warning",
        NC: "error",
        ND: "info",
      };
      return colors[docType] || "grey";
    },

    getAvailableCount(item) {
      const used = parseInt(item.Actual) - parseInt(item.AuthorizationFrom);
      const total = parseInt(item.AuthorizationTo) - parseInt(item.AuthorizationFrom) + 1;
      return Math.max(0, total - used);
    },

    getUsagePercentage(item) {
      const used = parseInt(item.Actual) - parseInt(item.AuthorizationFrom);
      const total = parseInt(item.AuthorizationTo) - parseInt(item.AuthorizationFrom) + 1;
      return Math.min(100, (used / total) * 100);
    },

    getUsageColor(item) {
      const percentage = this.getUsagePercentage(item);
      if (percentage > 90) return "error";
      if (percentage > 75) return "warning";
      return "success";
    },

    isActive(item) {
      const available = this.getAvailableCount(item);
      return available > 0;
    },

    getStatusColor(item) {
      const available = this.getAvailableCount(item);
      if (available === 0) return "error";
      if (available < 100) return "warning";
      return "success";
    },

    getStatusText(item) {
      const available = this.getAvailableCount(item);
      if (available === 0) return "Agotado";
      if (available < 100) return "Por agotar";
      return "Activo";
    },
  },
};
</script>

<style scoped>
.v-progress-circular {
  font-weight: bold;
  font-size: 12px;
}

.v-data-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.v-card {
  border-radius: 12px;
}

.v-dialog .v-card {
  border-radius: 16px;
}
</style>
